import { Injectable, Injector } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import ZoomVideo,
{
  VideoQuality,
  ParticipantPropertiesPayload,
  Participant,
  CommandChannel,
  ExecutedFailure,
  CommandChannelMsg
} from '@zoom/videosdk';
import { firstValueFrom, BehaviorSubject, Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { NotificationService } from './notification.service';
import { MediaService } from './media.service';
import { SessionService } from './session.service';
import { Router } from '@angular/router';
import { NetworkQuality, QualityTrend } from '../components/network-quality-indicator/network-quality-indicator.component';

export type CommandChannelResponse = CommandChannelMsg;
export type ZoomClientType = ReturnType<typeof ZoomVideo.createClient>;

export enum CommandEventType {
  IMAGE_CAPTURE_REQUESTED = 'Image Capture Requested',
  IMAGE_CAPTURE_ACKNOWLEDGE = 'Image Capture Acknowledge',
  IMAGE_CAPTURE_COMPLETED = 'Image Capture Completed',
  IMAGE_UPLOAD_COMPLETED = 'Image Upload Completed',
  CAMERA_GET_LIST_REQUESTED = 'Camera Get List Requested',
  CAMERA_GET_LIST_COMPLETED = 'Camera Get List Completed',
  CAMERA_SWITCH_REQUESTED = 'Camera Switch Requested',
  CAMERA_SWITCH_COMPLETED = 'Camera Switch Completed',
  CHAT_SEND_MESSAGE = 'Chat Send Message',
  CHAT_RECEIVE_MESSAGE = 'Chat Receive Message',
  VIDEO_RECORDING_STARTED = 'Video Recording Started',
  VIDEO_RECORDING_STOPPED = 'Video Recording Stopped',
  LOCATION_INSPECTOR_CHECK_IN = 'Location Inspector Check-In',
  LOCATION_TRACKING_STARTED = 'Location Tracking Started',
  LOCATION_PING_RECEIVED = 'Location Ping Received',
  LOCATION_MAP_READY = 'Location Map Ready',
  COORDINATOR_TEST = 'Coordinator Test',
  SESSION_END_REQUESTED = 'Session End Requested',
}

export interface CommandEvent {
  type: CommandEventType;
  data: any;
}

export interface NetworkQualityState {
  quality: NetworkQuality;
  trend: QualityTrend;
  latency?: number;
  uplink: number;
  downlink: number;
}

@Injectable({
  providedIn: 'root'
})
export class ZoomService {
  private static _client: ReturnType<typeof ZoomVideo.createClient> | null;
  private apiUrl = `${environment.apiUrl}`;
  public static videoQuality1080P: VideoQuality = VideoQuality.Video_1080P;
  private static _participantIdentity: Participant | null = null;
  private _inspectorCameraList: any[] | null = null;
  private mediaService: MediaService = {} as MediaService;
  private _zoomSesssionId: string | null = null;
  private sessionService!: SessionService;
  private logBuffer: any[] = [];
  private logBufferTimer: any = null;
  private statsCollectionInterval: any = null;
  private qualityHistory: number[] = [];
  private readonly qualityHistorySize = 5;
  private currentUplink: number = 3;
  private currentDownlink: number = 3;
  private networkQualitySubject = new BehaviorSubject<NetworkQualityState>({
    quality: NetworkQuality.GOOD,
    trend: QualityTrend.STABLE,
    uplink: 3,
    downlink: 3
  });

  constructor(
    private http: HttpClient,
    private notify: NotificationService,
    private injector: Injector,
    private router: Router,
  ) {
      console.log(window.isSecureContext);
      const mediaCompatibilty = ZoomVideo.checkSystemRequirements();
      const features = ZoomVideo.checkFeatureRequirements();
      console.log('Media Compatibility:', mediaCompatibilty);
      console.log('Feature Compatibility:', features);
      if (!ZoomService._client) {
        ZoomService._client = ZoomVideo.createClient();
        console.log(`ZoomVideo Client v${ZoomVideo.VERSION} initialized`);
      }

      setTimeout(() => {
        this.mediaService = this.injector.get(MediaService);
        this.sessionService = this.injector.get(SessionService);
      });

  }

  get client() {
    return ZoomService._client;
  }

  get participantIdentity(): Participant | null {
    return ZoomService._participantIdentity;
  }

  get inspector(): Participant | null {
    if (!ZoomService._client) {
      throw new Error('Zoom client not initialized');
    }
    return ZoomService._client?.getAllUser().find((participant) => participant.userIdentity?.toLowerCase() === 'inspector') || null;
  }

  get coordinator(): Participant | null {
    if (!ZoomService._client) {
      throw new Error('Zoom client not initialized');
    }
    return ZoomService._client.getAllUser().find((participant) => participant.userIdentity?.toLowerCase() === 'coordinator') || null;
  }

  get networkQuality$(): Observable<NetworkQualityState> {
    return this.networkQualitySubject.asObservable();
  }

  get currentNetworkQuality(): NetworkQualityState {
    return this.networkQualitySubject.value;
  }

  get inspectorCameraList(): any[] | null {
    return this._inspectorCameraList;
  }

  set inspectorCameraList(cameraList: any[] | null) {
    this._inspectorCameraList = cameraList;
  }

  get zoomSessionId(): string | null {
    return this._zoomSesssionId;
  }

  async connect(topic: string, userName: string, role: number = 1): Promise<ZoomClientType | void> {
    const joinStartTime = Date.now();
    try {
      if (!ZoomService._client) {
        throw new Error('Zoom client not initialized');
      }
      const response: any = await firstValueFrom(this.http.post(`${this.apiUrl}/govideo/token`, {
        sessionName: topic,
        role,
        sessionId: topic,
        userIdentity: userName,
      }));
      if (!response.token || !response.token.length) {
        throw new Error('Error getting zoom token');
      }
      await ZoomService._client.init('en-US', 'Global',
        {
          patchJsMedia: true,
          // enforceMultipleVideos: true,
          stayAwake: true,
          leaveOnPageUnload: true
        }
      );
      await ZoomService._client.join(topic, response.token, userName, '', 10);
      this._zoomSesssionId = ZoomService._client.getSessionInfo().sessionId;
      this.notify.showDebugToast(`Zoom Session ID: ${this._zoomSesssionId}`, JSON.stringify(ZoomService._client.getSessionInfo(), null, 2));
      ZoomService._participantIdentity = ZoomService._client.getCurrentUserInfo();
      this.sessionService.zoomSessionId = this._zoomSesssionId;
      this.sessionService.sessionId = topic;
      this.attachEventListeners();

      // Subscribe to Zoom SDK statistics
      this.subscribeToStatistics();

      // Start periodic stats collection (every 10 seconds)
      this.startStatsCollection();

      // Log successful join
      const joinDuration = Date.now() - joinStartTime;
      await this.logEvent({
        eventType: 'joined',
        severity: 'info',
        metadata: {
          joinDuration,
          zoomSdkVersion: ZoomVideo.VERSION,
        },
      });

      // Check permissions after joining
      await this.checkPermissions();

      return ZoomService._client;
    } catch (error) {
      console.error('Error initializing Zoom client:', error);

      // Log failed join attempt
      await this.logEvent({
        eventType: 'join_failed',
        severity: 'error',
        errorMessage: error instanceof Error ? error.message : String(error),
        metadata: { joinDuration: Date.now() - joinStartTime },
      });
    }
  }

  attachEventListeners(): void {
    ZoomService._client?.on('command-channel-message', async (payload: CommandChannelMsg) => {
      // this.notify.showDebugToast(`GoVideo Channel Message Sent`, payload?.text);
      console.log('Received GoVideo Channel Message:', payload);
      await this.handleCommandEvents(payload);
    });

    ZoomService._client?.on('video-quality-changed', async (event) => {
      const { quality } = event;
      console.log(`SDK Event: Video quality changed to ${quality}.`);

      await this.logEvent({
        eventType: 'quality_change',
        severity: 'info',
        metadata: { quality, event },
      });
    });

    ZoomService._client?.on('video-stream-started', async (event) => {
      const { userId } = event;
      console.log(`SDK Event: User ${userId} started video stream.`);

      await this.logEvent({
        eventType: 'video_started',
        severity: 'info',
        metadata: { userId, event },
      });
    });

    ZoomService._client?.on('video-stream-stopped', async (event) => {
      const { userId } = event;
      console.log(`SDK Event: User ${userId} stopped video stream.`);

      await this.logEvent({
        eventType: 'video_stopped',
        severity: 'info',
        metadata: { userId, event },
      });
    });

    // User joined event
    ZoomService._client?.on('user-added', async (event) => {
      console.log('SDK Event: User added', event);

      await this.logEvent({
        eventType: 'user_joined',
        severity: 'info',
        metadata: { event },
      });
    });

    // User left event
    ZoomService._client?.on('user-removed', async (event) => {
      console.log('SDK Event: User removed', event);

      await this.logEvent({
        eventType: 'user_left',
        severity: 'info',
        metadata: { event },
      });
    });

    // Connection quality events
    ZoomService._client?.on('connection-change', async (event) => {
      console.log('SDK Event: Connection changed', event);

      const severity = event.state === 'Connected' ? 'info' : 'warning';
      await this.logEvent({
        eventType: 'connection_change',
        severity,
        networkQuality: event.state,
        metadata: { event },
      });
    });

    // Peer disconnection
    ZoomService._client?.on('peer-video-state-change', async (event) => {
      console.log('SDK Event: Peer video state changed', event);

      await this.logEvent({
        eventType: 'peer_video_state_change',
        severity: 'info',
        metadata: { event },
      });
    });

    // Recording events
    ZoomService._client?.on('individual-recording-change', async (event: any) => {
      console.log('SDK Event: Recording state changed', event);

      await this.logEvent({
        eventType: event.state === 'Active' ? 'client_recording_started' : 'client_recording_stopped',
        severity: 'info',
        metadata: { event },
      });
    });

    // Media device errors
    ZoomService._client?.on('device-change', async (event: any) => {
      console.log('SDK Event: Device changed', event);

      await this.logEvent({
        eventType: 'device_change',
        severity: 'info',
        metadata: { event },
      });
    });

    // SDK errors
    ZoomService._client?.on('media-sdk-change', async (event: any) => {
      console.log('SDK Event: Media SDK change', event);

      await this.logEvent({
        eventType: 'sdk_event',
        severity: 'info',
        metadata: { event },
      });
    });

    // Network quality monitoring
    ZoomService._client?.on('network-quality-change', async (payload: any) => {
      console.log('SDK Event: Network quality changed', payload);

      // Zoom sends separate events for uplink and downlink with structure:
      // { type: 'uplink' | 'downlink', level: 0-5, userId: number }
      if (payload.type === 'uplink') {
        this.currentUplink = payload.level || 0;
      } else if (payload.type === 'downlink') {
        this.currentDownlink = payload.level || 0;
      }

      // Update network quality tracking with current values
      this.updateNetworkQuality(this.currentUplink, this.currentDownlink, payload.latency);

      const networkQuality = this.mapNetworkQuality(this.currentUplink, this.currentDownlink);
      const severity = (this.currentUplink < 2 || this.currentDownlink < 2) ? 'warning' : 'info';

      await this.logEvent({
        eventType: 'network_quality_change',
        severity,
        networkQuality,
        latency: payload.latency,
        metadata: {
          ...payload,
        },
      });
    });

    // Video statistics monitoring
    ZoomService._client?.on('video-statistic-data-change', async (payload: any) => {
      console.log('SDK Event: Video statistics changed', payload);

      await this.logEvent({
        eventType: 'video_stats_update',
        severity: 'info',
        framerate: payload.fps,
        resolution: payload.width && payload.height ? `${payload.width}x${payload.height}` : undefined,
        videoBitrate: payload.bitrate,
        metadata: {
          encoding: payload.encoding,
          userId: payload.userId,
          ...payload,
        },
      });
    });

    // Audio statistics monitoring
    ZoomService._client?.on('audio-statistic-data-change', async (payload: any) => {
      console.log('SDK Event: Audio statistics changed', payload);

      const severity = (payload.packetLoss && payload.packetLoss > 5) ? 'warning' : 'info';

      await this.logEvent({
        eventType: 'audio_stats_update',
        severity,
        packetLoss: payload.packetLoss,
        jitter: payload.jitter,
        audioBitrate: payload.bitrate,
        metadata: {
          sampleRate: payload.sampleRate,
          userId: payload.userId,
          ...payload,
        },
      });
    });

    // Screen share statistics monitoring
    ZoomService._client?.on('share-statistic-data-change', async (payload: any) => {
      console.log('SDK Event: Screen share statistics changed', payload);

      await this.logEvent({
        eventType: 'share_stats_update',
        severity: 'info',
        framerate: payload.fps,
        resolution: payload.width && payload.height ? `${payload.width}x${payload.height}` : undefined,
        metadata: {
          encoding: payload.encoding,
          userId: payload.userId,
          ...payload,
        },
      });
    });
  }

  async handleCommandEvents(payload: CommandChannelMsg): Promise<void> {
    const event = JSON.parse(payload.text);
    const receiver = (payload.senderId === this.coordinator?.userId) ? 'inspector' : 'coordinator';
    switch (event.type) {
      case CommandEventType.IMAGE_CAPTURE_REQUESTED:
        this.notify.showToast('info', 'GoVideo', 'Coordinator requested image');
        // Send device info so coordinator knows if stream will stop (all mobile devices stop stream for high-res capture)
        const isMobileDevice = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
        const commandResponse = await this.sendCommand({
          type: CommandEventType.IMAGE_CAPTURE_ACKNOWLEDGE,
          data: { isMobileDevice },
        }, this.coordinator?.userId || 0);
        const mediaFile = await this.mediaService.capturePhoto(this.sessionService.sessionId);
        if (mediaFile) {
          this.sessionService.incrementImageCount();
          const commandResponse = await this.sendCommand({
            type: CommandEventType.IMAGE_CAPTURE_COMPLETED,
            data: { imageUrl: mediaFile.url  }, 
          }, this.coordinator?.userId || 0);
        }
        break;
      case CommandEventType.IMAGE_CAPTURE_ACKNOWLEDGE:
        this.notify.showToast('info', 'GoVideo', 'Image capture acknowledged');
        break;
      case CommandEventType.IMAGE_CAPTURE_COMPLETED:
        this.sessionService.incrementImageCount();
        this.notify.showToast('info', 'GoVideo', 'Image capture completed');
        break;
      case CommandEventType.IMAGE_UPLOAD_COMPLETED:
        this.notify.showToast('info', 'GoVideo', 'Image uploaded successfully');
        break;
      case CommandEventType.CAMERA_GET_LIST_REQUESTED:
        this.notify.showToast('info', 'GoVideo', 'Inspector requested camera switch');
        try {
          const cameras = ZoomService._client
            ?.getMediaStream()
            .getCameraList();
          const CommandChannelResponse = await this.sendCommand({ type: CommandEventType.CAMERA_GET_LIST_COMPLETED, data: { cameras }}, this.coordinator?.userId || 0);
        } catch (error) {
          console.log(error);
        }
        break;
      case CommandEventType.CAMERA_GET_LIST_COMPLETED:
        this._inspectorCameraList = event.data.cameras;
        break;
      case CommandEventType.CAMERA_SWITCH_REQUESTED:
        await ZoomService._client?.getMediaStream().switchCamera(event.data.cameraId);
        this.notify.showToast('info', 'GoVideo', 'Camera has has been switched');
        const CommandChannelResponse = await this.sendCommand({ type: CommandEventType.CAMERA_SWITCH_COMPLETED, data: { cameraId: event.data.cameraId }}, this.coordinator?.userId || 0);
        break;
      case CommandEventType.CAMERA_SWITCH_COMPLETED:
        this.notify.showToast('success', 'GoVideo', 'Inspector camera switched');
        console.log(event.data.cameraId);
      break;
      case CommandEventType.CHAT_SEND_MESSAGE:
        this.notify.showToast('info', 'GoVideo', `Chat: ${event.data.message}`);
        break;
      case CommandEventType.CHAT_RECEIVE_MESSAGE:
        this.notify.showToast('info', 'GoVideo', `Chat: ${event.data.message}`);
        break;
      case CommandEventType.VIDEO_RECORDING_STARTED:
        this.notify.showToast('info', 'GoVideo', 'Video recording started');
        break;
      case CommandEventType.VIDEO_RECORDING_STOPPED:
        this.notify.showToast('info', 'GoVideo', 'Video recording stopped');
        break;
      case CommandEventType.LOCATION_INSPECTOR_CHECK_IN:
        this.notify.showToast('info', 'GoVideo', 'Location inspector checked-in');
        break;
      case CommandEventType.SESSION_END_REQUESTED:
        this.notify.showToast('info', 'GoVideo', 'Coordinator is ending the session');
        this.leave();
        this.notify.showToast('info', 'GoVideo', 'You have left the session');  
        setTimeout(async () => {this.router.navigate(['/session-end']);}, 5000);
        break;
      case CommandEventType.COORDINATOR_TEST:
        this.notify.showToast('info', 'GoVideo', 'Coordinator initiated test');
        break;
      default:
        console.log(`Unhandled command event: ${event.type}`);
        break;
    }
  }
        
  getParticipants(): Participant[] {
    return ZoomService._client ? ZoomService._client.getAllUser() : [];
  }

  on(event: string, callback: (payload: any) => void): void {
    ZoomService._client?.on(event, callback);
  }

  async leave(): Promise<void> {
    try {
      if (!ZoomService._client) {
        throw new Error('Zoom client not initialized');
      }

      // Stop periodic stats collection
      this.stopStatsCollection();

      // Log leave event before leaving
      await this.logEvent({
        eventType: 'left',
        severity: 'info',
      });

      // Flush any remaining logs
      await this.flushLogBuffer();

      const stream = ZoomService._client.getMediaStream();
      if (stream) {
        await stream.stopAudio();
        await stream.stopVideo();
      }
      const endSession = ZoomService._participantIdentity ? ZoomService._participantIdentity.isHost : false;
      const result = await ZoomService._client.leave(endSession);
      if (this.mediaService.localVideoStream) {
        this.mediaService.stopLocalMedia();
      }
      this.cleanup();
      console.log('Left the session successfully');
    } catch (error) {
      console.error('Error leaving and ending the session:', error);

      // Log error
      await this.logEvent({
        eventType: 'leave_failed',
        severity: 'error',
        errorMessage: error instanceof Error ? error.message : String(error),
      });
    }
  }

  destroyClient(): void {
    // ZoomVideo.destroyClient();
    // ZoomService._client = null;
  }


  cleanup(): void {
    // Add cleanup logic here
    return;
  }

  async getToken(topic: string, userName: string, role: number = 1): Promise<string | void> {
    try {
      const response: any = await firstValueFrom(this.http.post(`${this.apiUrl}/govideo/token`, {
        sessionName: topic,
        role,
        sessionId: topic,
        userIdentity: userName
      }));
      if (!response.token || !response.token.length) {
        throw new Error('Error getting zoom token');
      }
      return response.token;
    } catch (error) {
      console.error(error);
    }
  }

  async getMicList(): Promise<any[]> {
    return ZoomService._client?.getMediaStream().getMicList() || [];
  }

  async selectMic(deviceId: string): Promise<void> {
    await ZoomService._client?.getMediaStream().switchMicrophone(deviceId) || Promise.resolve();
  }

  async getSpeakerList(): Promise<any[]> {
    return ZoomService._client?.getMediaStream().getSpeakerList() || [];
  }

  async selectSpeaker(deviceId: string): Promise<void> {
    await ZoomService._client?.getMediaStream().switchSpeaker(deviceId) || Promise.resolve();
  }

  async sendCommand(commandEvent: CommandEvent, userId: number): Promise<ExecutedFailure | CommandChannelMsg> {
    const response = await ZoomService._client
      ?.getCommandClient()
      ?.send(JSON.stringify(commandEvent), userId);
    if (response && typeof response === 'object' && 'type' in response && 'reason' in response) {
      console.error('Error sending command:', response.reason);
      throw new Error(response.reason);
    }
    console.debug('Command sent successfully:', response);
    return response as CommandChannelMsg;
  }

  async apiZoomSessionDetails(zoomSessionId: string): Promise<any> {
    try {
      const response = await firstValueFrom(
        this.http.get(`${this.apiUrl}/zoom/${encodeURIComponent(encodeURIComponent(zoomSessionId))}`)
      );
      console.log('Zoom session retrieved:', response);
      return response;
    } catch (error) {
      console.error('Error retrieving Zoom session:', error);
      throw error;
    }
  }

  // ==================== Session Logging Methods ====================

  /**
   * Get browser and device information
   */
  private getBrowserInfo() {
    const ua = navigator.userAgent;
    let browser = 'Unknown';
    let browserVersion = 'Unknown';
    let os = 'Unknown';
    let device = 'Desktop';

    // Browser detection
    if (ua.includes('Chrome') && !ua.includes('Edg')) {
      browser = 'Chrome';
      browserVersion = ua.match(/Chrome\/(\d+\.\d+)/)?.[1] || 'Unknown';
    } else if (ua.includes('Safari') && !ua.includes('Chrome')) {
      browser = 'Safari';
      browserVersion = ua.match(/Version\/(\d+\.\d+)/)?.[1] || 'Unknown';
    } else if (ua.includes('Firefox')) {
      browser = 'Firefox';
      browserVersion = ua.match(/Firefox\/(\d+\.\d+)/)?.[1] || 'Unknown';
    } else if (ua.includes('Edg')) {
      browser = 'Edge';
      browserVersion = ua.match(/Edg\/(\d+\.\d+)/)?.[1] || 'Unknown';
    }

    // OS detection
    if (ua.includes('Win')) os = 'Windows';
    else if (ua.includes('Mac')) os = 'macOS';
    else if (ua.includes('Linux')) os = 'Linux';
    else if (ua.includes('Android')) os = 'Android';
    else if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';

    // Device detection
    if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
      device = 'Tablet';
    } else if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
      device = 'Mobile';
    }

    return { browser, browserVersion, os, device };
  }

  /**
   * Log session event to API
   */
  private async logEvent(eventData: any): Promise<void> {
    try {
      const { browser, browserVersion, os, device } = this.getBrowserInfo();
      const userIdentity = ZoomService._participantIdentity?.userIdentity || null;
      const userRole = ZoomService._participantIdentity?.isHost ? 1 : 0;

      const logEntry = {
        sessionId: this.sessionService?.sessionId || '',
        zoomSessionId: this._zoomSesssionId,
        source: 'client',
        userIdentity,
        userRole,
        browser,
        browserVersion,
        os,
        device,
        ...eventData,
      };

      // Buffer logs to send in batches
      this.logBuffer.push(logEntry);

      // Send buffered logs every 5 seconds or when buffer reaches 10 entries
      if (this.logBuffer.length >= 10) {
        this.flushLogBuffer();
      } else if (!this.logBufferTimer) {
        this.logBufferTimer = setTimeout(() => this.flushLogBuffer(), 5000);
      }
    } catch (error) {
      console.error('Error logging event:', error);
    }
  }

  /**
   * Send buffered logs to API
   */
  private async flushLogBuffer(): Promise<void> {
    if (this.logBuffer.length === 0) return;

    const logsToSend = [...this.logBuffer];
    this.logBuffer = [];

    if (this.logBufferTimer) {
      clearTimeout(this.logBufferTimer);
      this.logBufferTimer = null;
    }

    try {
      // Send logs in parallel
      await Promise.all(
        logsToSend.map(log =>
          firstValueFrom(this.http.post(`${this.apiUrl}/zoom/logs`, log))
            .catch(err => console.error('Error sending log:', err))
        )
      );
      console.log(`📊 Sent ${logsToSend.length} session logs to API`);
    } catch (error) {
      console.error('Error flushing log buffer:', error);
    }
  }

  /**
   * Check and log media permissions
   */
  private async checkPermissions(): Promise<void> {
    try {
      const permissions: any = {
        cameraPermission: false,
        microphonePermission: false,
      };

      try {
        const videoStream = await navigator.mediaDevices.getUserMedia({ video: true });
        permissions.cameraPermission = true;
        videoStream.getTracks().forEach(track => track.stop());
      } catch (e) {
        permissions.cameraPermission = false;
      }

      try {
        const audioStream = await navigator.mediaDevices.getUserMedia({ audio: true });
        permissions.microphonePermission = true;
        audioStream.getTracks().forEach(track => track.stop());
      } catch (e) {
        permissions.microphonePermission = false;
      }

      await this.logEvent({
        eventType: 'permissions_check',
        severity: (permissions.cameraPermission && permissions.microphonePermission) ? 'info' : 'warning',
        ...permissions,
      });
    } catch (error) {
      console.error('Error checking permissions:', error);
    }
  }

  /**
   * Get video/audio statistics from Zoom SDK
   */
  private async getMediaStats(): Promise<any> {
    try {
      const stream = ZoomService._client?.getMediaStream();
      if (!stream) return {};

      const stats: any = {};

      // Basic media state
      try {
        stats.isVideoOn = stream.isCapturingVideo();
        stats.isAudioMuted = stream.isAudioMuted();

        // Get system capabilities
        const maxVideos = stream.getMaxRenderableVideos?.();
        if (maxVideos !== undefined) {
          stats.maxRenderableVideos = maxVideos;
        }

        // Get active camera/mic info
        const activeMic = stream.getActiveMicrophone?.();
        const activeCamera = stream.getActiveCamera?.();
        const activeSpeaker = stream.getActiveSpeaker?.();

        if (activeMic) stats.activeMicrophone = activeMic;
        if (activeCamera) stats.activeCamera = activeCamera;
        if (activeSpeaker) stats.activeSpeaker = activeSpeaker;

        // Get connection type from navigator
        const connection = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;
        if (connection) {
          stats.connectionType = connection.effectiveType || connection.type || 'unknown';
          stats.downlink = connection.downlink; // Mbps
          stats.rtt = connection.rtt; // Round trip time in ms
        }

        // Get CPU usage estimate (if available via Performance API)
        if ('memory' in performance) {
          const memory = (performance as any).memory;
          stats.memoryUsed = memory.usedJSHeapSize;
          stats.memoryTotal = memory.totalJSHeapSize;
          stats.memoryLimit = memory.jsHeapSizeLimit;
        }

      } catch (e) {
        console.debug('Some stats not available:', e);
      }

      return stats;
    } catch (error) {
      console.error('Error getting media stats:', error);
      return {};
    }
  }

  /**
   * Subscribe to Zoom SDK statistics
   */
  private subscribeToStatistics(): void {
    try {
      const client = ZoomService._client as any;
      const stream = client?.getMediaStream();

      if (!client || !stream) {
        console.warn('Cannot subscribe to statistics - client or stream not available');
        return;
      }

      // Subscribe to video statistics
      try {
        if (typeof stream.subscribeVideoStatisticData === 'function') {
          stream.subscribeVideoStatisticData();
          console.log('✅ Subscribed to video statistics');
        }
      } catch (e) {
        console.debug('Video statistics subscription not available:', e);
      }

      // Subscribe to audio statistics
      try {
        if (typeof stream.subscribeAudioStatisticData === 'function') {
          stream.subscribeAudioStatisticData();
          console.log('✅ Subscribed to audio statistics');
        }
      } catch (e) {
        console.debug('Audio statistics subscription not available:', e);
      }

      // Subscribe to screen share statistics
      try {
        if (typeof stream.subscribeShareStatisticData === 'function') {
          stream.subscribeShareStatisticData();
          console.log('✅ Subscribed to share statistics');
        }
      } catch (e) {
        console.debug('Share statistics subscription not available:', e);
      }

      console.log('📊 Zoom SDK statistics subscriptions initialized');
    } catch (error) {
      console.error('Error subscribing to statistics:', error);
    }
  }

  /**
   * Start periodic statistics collection
   */
  private startStatsCollection(): void {
    try {
      // Clear any existing interval
      this.stopStatsCollection();

      // Collect stats every 10 seconds
      this.statsCollectionInterval = setInterval(async () => {
        await this.collectDetailedStats();
      }, 10000);

      console.log('📊 Started periodic stats collection (every 10 seconds)');
    } catch (error) {
      console.error('Error starting stats collection:', error);
    }
  }

  /**
   * Stop periodic statistics collection
   */
  private stopStatsCollection(): void {
    if (this.statsCollectionInterval) {
      clearInterval(this.statsCollectionInterval);
      this.statsCollectionInterval = null;
      console.log('📊 Stopped periodic stats collection');
    }
  }

  /**
   * Collect detailed statistics for session and per-user metrics
   */
  private async collectDetailedStats(): Promise<void> {
    try {
      if (!ZoomService._client) return;

      // Get session-level stats
      const mediaStats = await this.getMediaStats();
      const sessionInfo = ZoomService._client.getSessionInfo();
      const allUsers = ZoomService._client.getAllUser();

      // Calculate CPU usage estimate based on performance
      let cpuUsage: number | undefined;
      if (mediaStats.memoryUsed && mediaStats.memoryTotal) {
        // Rough estimate: memory pressure as proxy for CPU
        cpuUsage = Math.round((mediaStats.memoryUsed / mediaStats.memoryTotal) * 100);
      }

      // Log session-level aggregate metrics
      await this.logEvent({
        eventType: 'periodic_stats_collection',
        severity: 'info',
        connectionType: mediaStats.connectionType,
        bandwidth: mediaStats.downlink ? Math.round(mediaStats.downlink * 1000) : undefined, // Convert Mbps to Kbps
        latency: mediaStats.rtt,
        cpuUsage,
        metadata: {
          type: 'session_aggregate',
          sessionId: sessionInfo.sessionId,
          participantCount: allUsers.length,
          maxRenderableVideos: mediaStats.maxRenderableVideos,
          isVideoOn: mediaStats.isVideoOn,
          isAudioMuted: mediaStats.isAudioMuted,
          activeMicrophone: mediaStats.activeMicrophone,
          activeCamera: mediaStats.activeCamera,
          activeSpeaker: mediaStats.activeSpeaker,
          memoryUsed: mediaStats.memoryUsed,
          memoryTotal: mediaStats.memoryTotal,
          memoryLimit: mediaStats.memoryLimit,
        },
      });

      // Log per-user metrics (if we're capturing video from other users)
      for (const user of allUsers) {
        if (user.userId !== ZoomService._participantIdentity?.userId) {
          await this.logEvent({
            eventType: 'per_user_stats',
            severity: 'info',
            metadata: {
              type: 'per_user',
              userId: user.userId,
              userIdentity: user.userIdentity,
              displayName: user.displayName,
              isHost: user.isHost,
              bVideoOn: user.bVideoOn,
              audio: user.audio,
              muted: user.muted,
              sharerOn: user.sharerOn,
            },
          });
        }
      }
    } catch (error) {
      console.error('Error collecting detailed stats:', error);
    }
  }

  /**
   * Map network quality scores to readable quality levels
   * Zoom uses 0-5 scale: 0-1 = Poor, 2 = Normal, 3-5 = Good
   */
  private mapNetworkQuality(uplink: number, downlink: number): string {
    const avg = (uplink + downlink) / 2;

    if (avg >= 3) return 'good';
    if (avg >= 2) return 'fair';
    return 'poor';
  }

  /**
   * Map network quality scores to NetworkQuality enum
   * Zoom uses 0-5 scale: 0-1 = Very Poor/Poor, 2 = Fair, 3-4 = Good, 4.5-5 = Excellent
   */
  private mapNetworkQualityEnum(uplink: number, downlink: number): NetworkQuality {
    const avg = (uplink + downlink) / 2;

    if (avg >= 4.5) return NetworkQuality.EXCELLENT;
    if (avg >= 3.5) return NetworkQuality.GOOD;
    if (avg >= 2.5) return NetworkQuality.FAIR;
    if (avg >= 1.5) return NetworkQuality.POOR;
    return NetworkQuality.VERY_POOR;
  }

  /**
   * Calculate quality trend based on recent history
   * Compares recent average to older average
   */
  private calculateQualityTrend(): QualityTrend {
    if (this.qualityHistory.length < 3) {
      return QualityTrend.STABLE;
    }

    // Compare last 2 readings vs previous 3 readings
    const recentCount = Math.min(2, this.qualityHistory.length - 1);
    const olderCount = Math.min(3, this.qualityHistory.length - recentCount);

    const recentReadings = this.qualityHistory.slice(-recentCount);
    const olderReadings = this.qualityHistory.slice(-(recentCount + olderCount), -recentCount);

    const recentAvg = recentReadings.reduce((a, b) => a + b, 0) / recentReadings.length;
    const olderAvg = olderReadings.reduce((a, b) => a + b, 0) / olderReadings.length;

    const difference = recentAvg - olderAvg;

    // Use threshold to avoid flapping
    if (difference > 0.5) return QualityTrend.IMPROVING;
    if (difference < -0.5) return QualityTrend.DEGRADING;
    return QualityTrend.STABLE;
  }

  /**
   * Update network quality tracking
   */
  private updateNetworkQuality(uplink: number, downlink: number, latency?: number): void {
    const avg = (uplink + downlink) / 2;

    // Add to history
    this.qualityHistory.push(avg);
    if (this.qualityHistory.length > this.qualityHistorySize) {
      this.qualityHistory.shift();
    }

    // Calculate quality and trend
    const quality = this.mapNetworkQualityEnum(uplink, downlink);
    const trend = this.calculateQualityTrend();

    // Enhanced logging to debug quality values
    console.log('[Network Quality Update]', {
      uplink,
      downlink,
      avg,
      quality,
      trend,
      latency,
      history: [...this.qualityHistory]
    });

    // Emit update
    this.networkQualitySubject.next({
      quality,
      trend,
      latency,
      uplink,
      downlink
    });
  }
}